import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdOutlineArrowBackIos } from "react-icons/md";

const USERS = [
  {
    id: 1,
    name: "John Smith",
    email: "john_smith@vetcare.com",
    role: "Super Admin",
    lastLogin: "2025-01-13 10:30 AM",
    status: "Active",
  },
  {
    id: 2,
    name: "John Smith",
    email: "john_smith@vetcare.com",
    role: "Moderator",
    lastLogin: "2025-01-13 10:30 AM",
    status: "Inactive",
  },
  {
    id: 3,
    name: "John Smith",
    email: "john_smith@vetcare.com",
    role: "Finance Admin",
    lastLogin: "2025-01-13 10:30 AM",
    status: "Active",
  },
  {
    id: 4,
    name: "John Smith",
    email: "john_smith@vetcare.com",
    role: "Content Admin",
    lastLogin: "2025-01-13 10:30 AM",
    status: "Inactive",
  },
];

const MODULES = [
  "User Management",
  "Financial Data",
  "Content Moderation",
  "System Config",
  "Reports",
];

const ROLES = ["Super Admin", "Moderator", "Finance", "Content"];

/* ───────────────────────────────────────────────
   Simple blue / gray toggle pill
──────────────────────────────────────────────── */
function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={onChange}
      className={`h-4 w-7 rounded-full flex items-center transition-colors
        ${checked ? "bg-[#2B80FF]" : "bg-gray-300"}`}
    >
      <span
        className={`h-3 w-3 rounded-full bg-white shadow transform duration-200
          ${checked ? "translate-x-3.5" : "translate-x-0.5"}`}
      />
    </button>
  );
}

/* ──────��──────────────────��─────────────────────
   Settings page
──────────────────────────────────────────────── */
export default function Settings() {
  const navigate = useNavigate();

  /* permissionMatrix is a Map<module, Map<role, boolean>> */
  const [permissionMatrix, setPermissionMatrix] = useState(() => {
    const initial = {};
    MODULES.forEach((m) => {
      initial[m] = {};
      ROLES.forEach((r) => (initial[m][r] = r === "Super Admin")); // Super Admin ON by default
    });
    return initial;
  });

  const togglePermission = (module, role) =>
    setPermissionMatrix((prev) => ({
      ...prev,
      [module]: { ...prev[module], [role]: !prev[module][role] },
    }));

  return (
    <div className="min-h-screen bg-white font-inter px-6 pt-12">
      {/* ───────────── Header / Breadcrumb ───────────── */}
      <div className="flex items-start mb-10">
        <button
          onClick={() => navigate(-1)}
          className="mr-3 p-1 text-2xl text-gray-800 hover:text-blue-600"
        >
          <MdOutlineArrowBackIos />
        </button>
        <h1 className="text-2xl font-bold">Settings</h1>
      </div>

      {/* ───────────── Tabs (only one active now) ───────────── */}
      <div className="mb-8 ">
        <button
          className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium
                     bg-white "
        >
          Roles &amp; Permissions
        </button>
      </div>

      {/* ───────────── User Management ───────────── */}
      <section className="mb-16">
        <h2 className="text-lg font-semibold mb-1">User Management</h2>
        <p className="text-sm text-gray-500 mb-6">
          Manage admin roles and permissions across the platform
        </p>

        {/* Table header */}
        <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 mb-3">
          <div className="col-span-4">User</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-3">Last Login</div>
          <div className="col-span-3">Status</div>
        </div>

        {/* Table rows */}
        {USERS.map((u) => (
          <div
            key={u.id}
            className="grid grid-cols-12 gap-2 py-3  text-sm items-center"
          >
            {/* User */}
            <div className="col-span-4">
              <div className="font-medium text-black">{u.name}</div>
              <div className="text-gray-500 -mt-0.5">{u.email}</div>
            </div>

            {/* Role */}
            <div className="col-span-2 text-gray-700">{u.role}</div>

            {/* Last login */}
            <div className="col-span-3 text-gray-700">{u.lastLogin}</div>

            {/* Status */}
            <div className="col-span-3">
              <span
                className={`px-3 py-0.5 rounded-full text-xs font-semibold
                  ${u.status === "Active"
                    ? "bg-green-100 text-green-600"
                    : "bg-red-100 text-red-600"}`}
              >
                {u.status}
              </span>
            </div>
          </div>
        ))}
      </section>

      {/* ───────────── Permission Matrix ───────────── */}
      <section className="mb-24">
        <h2 className="text-lg font-semibold mb-1">Permission Matrix</h2>
        <p className="text-sm text-gray-500 mb-6">
          Configure role-based access control
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className="text-left font-semibold py-3 pr-6">Module/Access</th>
                {ROLES.map((r) => (
                  <th key={r} className="text-center font-semibold px-4">
                    {r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODULES.map((mod) => (
                <tr key={mod} className="">
                  {/* Module name */}
                  <td className="py-4 pr-6 font-medium text-gray-700">{mod}</td>

                  {/* Toggles */}
                  {ROLES.map((role) => (
                    <td key={role} className="text-center">
                      <Toggle
                        checked={permissionMatrix[mod][role]}
                        onChange={() => togglePermission(mod, role)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
